import { Box, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateSchemaForm, useMoveFormSchemaReport } from 'src/api/form-schema'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MainButton } from 'src/components/MainButton'
import { FormFieldType } from 'src/enums'
import { FormRow, getFieldKey } from 'src/generated-types/form-field'
import {
  IFormSchemaReport,
  IMoveFormSchemaReportDto,
  IMoveReportDestinationPickerProps,
  IMoveReportTarget,
} from 'src/generated-types/form-schema'
import { ShowDialogProps } from 'src/show-dialog'
import { ZodObject, z } from 'zod'
import FormSchemaForm from './FormSchemaForm'

interface IMoveReportDialogProps extends ShowDialogProps {
  customerId: EntityId
  excludedId: EntityId
  localityId?: EntityId
  report: IFormSchemaReport
  DestinationPicker: React.FC<IMoveReportDestinationPickerProps>
  onMoved?: () => void
}

const buildFormFromReport = (report: IFormSchemaReport, requiredFieldMessage: (name: string) => string) => {
  const layouts: FormRow[] = JSON.parse(report.formSchemaVersion?.layout || '[]')
  const storedValues = report.values ? JSON.parse(report.values) : {}
  let schema: ZodObject<any> = z.object({})
  const initialValues: Record<string, unknown> = {}

  layouts.forEach(row =>
    row.columns?.forEach(col =>
      col?.fields.forEach(field => {
        const key = getFieldKey(field)
        if (field.type !== FormFieldType.title) {
          initialValues[key] = storedValues[key] ?? storedValues[field.name] ?? ''
        }
        schema = schema.merge(
          z.object({
            [key]: field.isRequired
              ? z.string().min(1, { message: requiredFieldMessage(field.name) })
              : z.string().optional(),
          }),
        )
      }),
    ),
  )

  return { schema, initialValues }
}

const MoveReportDialog: React.FC<IMoveReportDialogProps> = ({
  customerId,
  excludedId,
  localityId,
  report,
  DestinationPicker,
  pop,
  onMoved,
}) => {
  const { t } = useTranslation(['form-schema-report', 'common', 'forms'])
  const [destination, setDestination] = useState<IMoveReportTarget>()
  const [isEditing, setIsEditing] = useState(false)
  const [isMoving, setIsMoving] = useState(false)
  const { mutateAsync: moveReport } = useMoveFormSchemaReport(customerId, report.id as EntityId)

  const isDraft = Boolean(report.isDraft)
  const formName = report.formSchemaVersion?.formSchema?.name ?? ''

  const { schema, initialValues } = useMemo(
    () => buildFormFromReport(report, name => t('requiredField', { ns: 'forms' }).replace('{0}', name)),
    [report, t],
  )

  const submitMove = async (payload: Omit<IMoveFormSchemaReportDto, 'targetId'>) => {
    if (!destination?.id) return
    setIsMoving(true)
    try {
      await moveReport({ targetId: Number(destination.id), ...payload })
      toast.success(t('report-moved-successfully', { ns: 'form-schema-report' }))
      invalidateSchemaForm.getFormSchemaReports(customerId)
      invalidateSchemaForm.getFilledFormSchemaReports(customerId)
      invalidationActivityLogCache.getMainComponentActivityLogs()
      onMoved?.()
      pop()
    } catch {
      toast.error(t('error-moving-report', { ns: 'form-schema-report' }))
    } finally {
      setIsMoving(false)
    }
  }

  if (isEditing && destination) {
    return (
      <FormSchemaForm
        schema={schema}
        version={report.formSchemaVersion!}
        title={formName}
        initialValues={initialValues}
        pop={() => setIsEditing(false)}
        isDraft={isDraft}
        submitText={t('save-and-move', { ns: 'form-schema-report' })}
        onSubmit={values => submitMove({ values: JSON.stringify(values), publishDraft: isDraft })}
        onSaveDraft={
          isDraft ? values => submitMove({ values: JSON.stringify(values), publishDraft: false }) : undefined
        }
        draftButtonText={t('draft-and-move', { ns: 'form-schema-report' })}
      />
    )
  }

  return (
    <GenericDialogLayout
      title={t('move-report', { ns: 'form-schema-report' })}
      titleTypography={{ variant: 'h6', fontWeight: 600, sx: { color: 'text.primary' } }}
      titleSectionTopPadding="16px"
      titleSectionBottomPadding="8px"
      titleSectionXPadding="20px"
      titleDivider
      actionsDivider
      removedCloseButton={false}
      pop={pop}
      sx={{ px: 2.5, pt: 1.5, pb: 1 }}
      actions={[
        <MainButton
          key="edit-and-move"
          variant="outlined"
          disabled={!destination || isMoving}
          onClick={() => setIsEditing(true)}
        >
          {t('open-and-edit', { ns: 'form-schema-report' })}
        </MainButton>,
        <MainButton key="move" loading={isMoving} disabled={!destination} onClick={() => submitMove({})}>
          {t('move', { ns: 'form-schema-report' })}
        </MainButton>,
      ]}
    >
      <Box display="flex" flexDirection="column" gap={1.5}>
        <DestinationPicker
          customerId={customerId}
          excludedId={excludedId}
          localityId={localityId}
          selected={destination}
          onSelect={setDestination}
        />
        {destination && (
          <Typography variant="body2">
            {t('moving-to', { ns: 'form-schema-report' })}:{' '}
            <Typography component="span" variant="body2" fontWeight={600}>
              {destination.label}
            </Typography>
          </Typography>
        )}
      </Box>
    </GenericDialogLayout>
  )
}

export default MoveReportDialog
